"use client"

import { Dialog, Transition } from "@headlessui/react"
import { ArrowRightMini } from "@medusajs/icons"
import { clx } from "@medusajs/ui"
import { type CSSProperties, Fragment, useMemo, useState } from "react"

import LocalizedClientLink from "@modules/common/components/localized-client-link"

export type NavMenuLink = {
  id: string
  name: string
  href: string
  target?: string
  isExternal?: boolean
  customColor?: string | null
  parentEffectClass?: "lealti-neon" | "lealti-neon-2" | "lealti-dance" | null
  children?: NavMenuLink[]
}

export type NavCategory = NavMenuLink

export type SideMenuTheme = {
  backgroundColor?: string
  textColor?: string
  iconColor?: string
  hoverColor?: string
  hoverBorderColor?: string
}

export const FALLBACK_SIDE_MENU_ITEMS: NavCategory[] = [
  { id: "home", name: "Inicio", href: "/" },
  { id: "shop", name: "Tienda", href: "/store" },
  { id: "account", name: "Cuenta", href: "/account" },
  { id: "cart", name: "Carrito", href: "/cart" },
]

const SideMenu = ({
  categories,
  theme,
  menuButtonColor,
}: {
  categories?: NavCategory[]
  theme?: SideMenuTheme
  menuButtonColor?: string
}) => {
  const resolvedTheme = useMemo(
    () => ({
      backgroundColor: theme?.backgroundColor || "rgba(255, 255, 255, 1)",
      textColor: theme?.textColor || "rgba(0, 0, 0, 1)",
      iconColor: theme?.iconColor || menuButtonColor || "rgba(0, 0, 0, 1)",
      hoverColor: theme?.hoverColor || "rgba(0, 0, 0, 1)",
      hoverBorderColor: theme?.hoverBorderColor || "rgba(0, 0, 0, 0.2)",
    }),
    [menuButtonColor, theme]
  )

  const themeStyles = useMemo(
    () =>
      ({
        "--storefront-side-menu-bg": resolvedTheme.backgroundColor,
        "--storefront-side-menu-text": resolvedTheme.textColor,
        "--storefront-side-menu-icon": resolvedTheme.iconColor,
        "--storefront-side-menu-hover": resolvedTheme.hoverColor,
        "--storefront-side-menu-hover-border": resolvedTheme.hoverBorderColor,
      }) as CSSProperties,
    [resolvedTheme]
  )

  const [openItemIds, setOpenItemIds] = useState<Record<string, boolean>>({})

  const toggleItem = (itemId: string) => {
    setOpenItemIds((state) => ({ ...state, [itemId]: !state[itemId] }))
  }

  const [open, setOpen] = useState(false)

  const renderMenuLink = (
    item: NavMenuLink,
    {
      className,
      onClick,
      testId,
      depth,
    }: {
      className: string
      onClick?: () => void
      testId?: string
      depth: number
    }
  ) => {
    const linkStyle = item.customColor
      ? ({ color: item.customColor } as CSSProperties)
      : undefined
    const effectClassName =
      depth === 0 && item.parentEffectClass ? ` ${item.parentEffectClass}` : ""
    const resolvedClassName = `${className}${effectClassName}`

    if (item.isExternal) {
      return (
        <a
          href={item.href}
          className={resolvedClassName}
          style={linkStyle}
          onClick={onClick}
          data-testid={testId}
        >
          {item.name}
        </a>
      )
    }

    return (
      <LocalizedClientLink
        href={item.href}
        className={resolvedClassName}
        style={linkStyle}
        onClick={onClick}
        data-testid={testId}
        target={item.target}
      >
        {item.name}
      </LocalizedClientLink>
    )
  }

  const toTestId = (value: string) =>
    value
      .toLowerCase()
      .trim()
      .replace(/\s+/g, "-")
      .replace(/[^a-z0-9_-]/g, "")

  const renderMenuItems = (items: NavMenuLink[], depth = 0) => (
    <ul
      className={
        depth === 0
          ? "flex flex-col gap-6 items-start justify-start"
          : "mt-2 pl-4 flex flex-col gap-2"
      }
    >
      {items.map((item) => {
        const hasChildren =
          Array.isArray(item.children) && item.children.length > 0
        const isExpanded = Boolean(openItemIds[item.id])
        return (
          <li key={item.id} className="w-full">
            <div className="flex items-center justify-between w-full">
              {renderMenuLink(item, {
                className:
                  depth === 0
                    ? "storefront-side-menu-link text-lg md:text-xl border-b border-transparent"
                    : "storefront-side-menu-link text-md md:text-lg border-b border-transparent",
                onClick: () => setOpen(false),
                testId:
                  depth === 0
                    ? `${toTestId(item.name)}-link`
                    : `mobile-${toTestId(item.name)}-link`,
                depth,
              })}

              {hasChildren ? (
                <button
                  aria-expanded={isExpanded}
                  onClick={() => toggleItem(item.id)}
                  className="storefront-side-menu-toggle ml-2 rounded-full border p-1 transition"
                  style={{ borderColor: "transparent" }}
                  data-testid={`mobile-toggle-${toTestId(item.name)}`}
                >
                  <ArrowRightMini
                    className={clx(
                      "transition-transform duration-150",
                      isExpanded ? "rotate-90" : ""
                    )}
                  />
                </button>
              ) : null}
            </div>

            {hasChildren && isExpanded
              ? renderMenuItems(item.children!, depth + 1)
              : null}
          </li>
        )
      })}
    </ul>
  )

  return (
    <div className="h-full storefront-side-menu" style={themeStyles}>
      <div className="flex items-center h-full">
        <div className="relative flex h-full">
          <button
            data-testid="nav-menu-button"
            className="storefront-side-menu-button relative h-full flex items-center px-2 md:px-4 transition-all ease-out duration-200 focus:outline-none border-b"
            onClick={() => setOpen(true)}
            aria-label="Abrir menú"
            style={{
              color: "var(--storefront-side-menu-icon, rgba(0, 0, 0, 1))",
              borderColor: "transparent",
            }}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              className="h-5 w-5 md:h-6 md:w-6"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M3 6h18M3 12h18M3 18h18"
              />
            </svg>
          </button>
        </div>

        <Transition show={open} as={Fragment}>
          <Dialog
            as="div"
            className="relative z-[70]"
            onClose={() => setOpen(false)}
          >
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-150"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <div className="fixed inset-0 bg-black/30 backdrop-blur-sm" />
            </Transition.Child>

            <div className="fixed inset-y-0 left-0 flex max-w-full">
              <Transition.Child
                as={Fragment}
                enter="transform transition ease-out duration-200"
                enterFrom="-translate-x-full"
                enterTo="translate-x-0"
                leave="transform transition ease-in duration-150"
                leaveFrom="translate-x-0"
                leaveTo="-translate-x-full"
              >
                <Dialog.Panel
                  className="pointer-events-auto flex h-full w-screen max-w-[420px] flex-col shadow-[0_32px_80px_rgba(15,23,42,0.18)]"
                  style={{
                    backgroundColor:
                      "var(--storefront-side-menu-bg, rgba(255, 255, 255, 1))",
                    color: "var(--storefront-side-menu-text, rgba(0, 0, 0, 1))",
                  }}
                >
                  <div className="flex flex-col h-full">
                    <header className="flex items-start justify-between px-6 pb-6 pt-6">
                      <div>
                        <Dialog.Title className="text-[22px] font-lealti-light font-bold">
                          Menú
                        </Dialog.Title>
                      </div>
                      <button
                        type="button"
                        onClick={() => setOpen(false)}
                        className="storefront-side-menu-close rounded-full p-2 transition border"
                        aria-label="Cerrar menú"
                        style={{ borderColor: "transparent" }}
                      >
                        <span className="sr-only">Cerrar</span>
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          className="h-5 w-5"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M6 18L18 6M6 6l12 12"
                          />
                        </svg>
                      </button>
                    </header>

                    <div className="flex-1 overflow-y-auto px-6 pb-6 pt-6">
                      {renderMenuItems(
                        categories && categories.length
                          ? categories
                          : FALLBACK_SIDE_MENU_ITEMS
                      )}
                    </div>

                    <div className="flex flex-col gap-y-6 px-6 pb-6">
                      <p className="storefront-side-menu-footer flex justify-between txt-compact-small">
                        © 2026 – Todos los derechos reservados
                      </p>
                    </div>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </Dialog>
        </Transition>
      </div>
    </div>
  )
}

export default SideMenu
